// formatToCurrency: lightweight manual formatter — prefixes a currency symbol
// (default "$") and thousands-separates a fixed 2-decimal value, e.g. "$ 1,234.50".
// Distinct from formatCurrency below, which uses Intl.NumberFormat + locale/ISO code.
export const formatToCurrency = (value, currency = '$') => {
    return currency +" "+ parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }

export const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return ""; // Return empty string if date is invalid
  
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
  
    return ` ${month} ${year}`;
  };

  export const formatDateToMonthYear = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return ""; // Return empty string if date is invalid
    const month = date.toLocaleString('default', { month: '2-digit' });
    const year = date.getFullYear();
    return `${year}-${month}`;
  };

  export const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return ""; // Return empty string if date is invalid
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
  
    return `${day} ${month} ${year}`;
  };

  export const percentageAchieved = (spent, budgeted) => {
    let value = (spent&&budgeted?((spent/budgeted)*100): 0)
    return value.toFixed(2) 
  }

   // Helper function to get the last day of the month from currentDate
   export const getLastDayOfMonth = (dateStr) => {
    const [year, month] = dateStr.split('-');
    return new Date(year, month, 0).toISOString().slice(0, 10); // Format: 'YYYY-MM-DD'
  };

  // Function to format a date to 'yyyy-MM-dd'
export const formatDateToInput = (dateStr) => {
  // Ensure dateStr is a string, otherwise return an empty string
  if (typeof dateStr !== 'string') return '';

  // Trim whitespace
  const trimmedDate = dateStr.trim();

  // Validate the input format (expects 'yyyy-MM-dd')
  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(trimmedDate);
  if (!isValidFormat) return '';

  return trimmedDate;
};

// Helper function to get the previous month in 'YYYY-MM' format
export const getPreviousMonth = (dateString) => {
  const [year, month] = dateString.split('-').map(Number);
  const newDate = new Date(year, month - 2, 1); // month is 0-indexed
  return newDate.toISOString().slice(0, 7);
};

// Helper function to get the first day of the current month
export const getFirstDayOfMonth = (date) => {
  const [year, month] = date.split('-');
  return `${year}-${month}-01`;
};

// formatCurrency: locale-aware formatter via Intl.NumberFormat. Expects an ISO
// currency code (default "GHS") and locale (default "en-GH") and renders the
// localized symbol/placement, e.g. "GHS 1,234.50". Distinct from formatToCurrency above.
export const formatCurrency = (amount, currency = 'GHS', locale = 'en-GH') => {
  const numericAmount = Number(amount);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(Number.isFinite(numericAmount) ? numericAmount : 0);
};

export const handleOutsideClick = (menuRef, buttonRef, event, setOpenDropdwon) => {
  // Check if the click is outside both the menu and the button
  if (
    menuRef.current &&
    !menuRef.current.contains(event.target) &&
    buttonRef.current &&
    !buttonRef.current.contains(event.target)
  ) {
    setOpenDropdwon(false);
  }
};

export const calculatePercentageChange = (current, previous) => {
  if (previous === 0) return current === 0 ? 0 : 100; // Handle divide by zero
  return ((current - previous) / previous) * 100;
};

export const getMonthDateRange = (year, month) => {
  if (!year || !month) return null; // no filtering

  const startDate = new Date(year, month - 1, 1, 0, 0, 0);
  // last day of month
  const endDate = new Date(year, month, 0, 23, 59, 59);
  return { startDate, endDate };
};

// helpers/groupBudgets.js
// helpers/groupBudgets.js
export const groupBudgetsByMonth = (budgets) => {
  const currentDate = new Date();
  const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  
  const grouped = {};

  budgets.forEach((budget) => {
    // Check if this budget has monthlyData for current month
    const hasCurrentMonthData = budget.monthlyData && budget.monthlyData[currentMonthYear];
    
    const groupKey = hasCurrentMonthData 
      ? currentMonthYear 
      : budget.startDate || budget.metadata?.startDate;
    
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    
    grouped[groupKey].push(budget);
  });

  return grouped;
};

export const formatGroupKey = (key) => {
  if (!key) return 'Unknown';
  
  const [year, month] = key.split('-');
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// New function to sort month-year keys chronologically
export const sortMonthYearKeys = (keys) => {
  return [...keys].sort((a, b) => {
    const [aYear, aMonth] = a.split('-').map(Number);
    const [bYear, bMonth] = b.split('-').map(Number);
    
    // First compare years
    if (aYear !== bYear) return aYear - bYear;
    // If same year, compare months
    return aMonth - bMonth;
  });
};

export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const computeOverspend = ({
  existing = [],
  draft,
  monthlyIncome = 0,
  budgetId,
  budgetPeriod,
  editingTx = null,
}) => {
  const baseExpense = existing
    .filter(
      (t) =>
        t.type === "expense" &&
        t.budgetId === budgetId &&
        t.budgetPeriod === budgetPeriod &&
        (!editingTx || t.id !== editingTx.id)
    )
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const newAmt = Number(draft.amount || 0);
  const newIsExpense = draft.type === "expense";

  let delta = 0;
  if (!editingTx) {
    delta = newIsExpense ? newAmt : 0;
  } else {
    const oldIsExpense =
      editingTx.type === "expense" &&
      editingTx.budgetId === budgetId &&
      editingTx.budgetPeriod === budgetPeriod;

    const oldAmt = Number(editingTx.amount || 0);

    if (oldIsExpense && newIsExpense) delta = newAmt - oldAmt;
    else if (!oldIsExpense && newIsExpense) delta = newAmt;
    else if (oldIsExpense && !newIsExpense) delta = -oldAmt;
    else delta = 0;
  }

  const projectedExpense = baseExpense + delta;
  return {
    projectedExpense,
    willOverspend: monthlyIncome > 0 && projectedExpense > monthlyIncome,
  };
}

export const cleanForFirestore = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

const MONEY_REGEX = /^\d*\.?\d{0,2}$/;
export const isValidMoney = (v) => MONEY_REGEX.test(String(v ?? "").trim());
export const normalizeMoney = (v) => {
  if (!v) return "";
  const num = parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return isNaN(num) ? "" : num.toFixed(2);
};
export const initializeEmptyAmounts = (months) =>
  Object.fromEntries(months.map((m) => [m, ""]));
